import { Handshake, ShieldCheck, GlobeLock, HelpCircle, XCircle } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { FormEvent, useState } from 'react';
import { useRouter } from '@tanstack/react-router';
import { useDialog } from 'src/features/plugins/outlook/contexts/DialogContext';
import { getApiUrl } from 'src/features/auth/legacy/getApiUrl';
import type { AuthOptions } from 'src/features/auth/legacy/auth.utils';
import { SsoMethodButtons, getSortedSsoMethods } from 'src/features/auth/legacy/SsoMethodButtons';
import { Card } from 'src/shared/components/ui/card';
import { Button } from 'src/shared/components/ui/buttons/button';
import mixpanel from 'src/shared/lib/mixpanel';
import AnimatedEllipsis from 'src/shared/components/ui/loaders/animated-ellipsis';
import { Separator } from 'src/shared/components/ui/separator';
import InputWithLabel from 'src/shared/components/ui/inputs/input-with-label';
import WithTooltip from 'src/shared/components/utils/WithTooltip';
import { identityProviders } from 'src/shared/constants/uiConfig.constant';

function normalizeSubdomainInput(input: string): { inputValue: string; discoveryDomain: string | null } {
  let value = input.trim();

  if (!value) {
    return { inputValue: '', discoveryDomain: null };
  }

  // Handle full URLs like https://tekst.tekst.com/ddd
  if (/^https?:\/\//i.test(value)) {
    try {
      const url = new URL(value);
      value = url.hostname;
    } catch {
      // Fall back to raw value
    }
  }

  // Strip any path fragments if not already handled
  if (value.includes('/')) {
    value = value.split('/')[0];
  }

  // Remove leading www. and trailing dot
  value = value.replace(/^www\./i, '').replace(/\.$/, '');

  let host: string;
  let inputValue: string;

  if (value.toLowerCase().endsWith('.tekst.com')) {
    host = value.toLowerCase();
    inputValue = host.replace(/\.tekst\.com$/i, '');
  } else if (!value.includes('.')) {
    inputValue = value.toLowerCase();
    host = `${inputValue}.tekst.com`;
  } else {
    // Fallback: take the first label as subdomain and normalize to tekst.com
    const firstLabel = value.split('.')[0].toLowerCase();
    inputValue = firstLabel;
    host = `${firstLabel}.tekst.com`;
  }

  return { inputValue, discoveryDomain: host };
}

export default function LoginCard() {
  const { loginWithRedirect, handleRedirectCallback } = useAuth0();
  const { openOfficeDialog, closeDialog, isDialogOpen } = useDialog();
  const router = useRouter();
  // Make SSO available for all Outlook add-in users (previously limited to a single inbox)
  const showSsoButton = true;
  const [view, setView] = useState<'login' | 'sso'>('login');
  const [subdomain, setSubdomain] = useState('');
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoError, setSsoError] = useState<string | null>(null);
  const [ssoAuthOptions, setSsoAuthOptions] = useState<AuthOptions | null>(null);

  const ssoMethods = ssoAuthOptions ? getSortedSsoMethods(ssoAuthOptions.authMethods) : [];
  const hasSsoMethods = ssoMethods.length > 0;

  const processMessage = async (args: { message: string; origin: string | undefined } | { error: number }) => {
    if ('error' in args) {
      console.error('[LoginCard] Dialog error event received:', args.error);
      closeDialog();
      return;
    }
    const messageFromDialog = JSON.parse(args.message);
    console.log('[LoginCard] Dialog message received:', messageFromDialog);
    closeDialog();

    if (messageFromDialog.status === 'success') {
      try {
        console.log('[LoginCard] Handling redirect callback with URL:', messageFromDialog.urlWithAuthInfo);
        // Reset UI back to a clean login state in case user was on the SSO tab
        setView('login');
        setSsoError(null);
        setSsoAuthOptions(null);
        setSubdomain('');
        mixpanel.track('Outlook Add-In Log In');
        await handleRedirectCallback(messageFromDialog.urlWithAuthInfo);
        await new Promise((resolve) => setTimeout(resolve, 100));
        console.log('[LoginCard] Redirect callback complete, navigating to taskpane.');
        router.history.push('/plugins/outlook/taskpane');
      } catch (error) {
        console.error('[LoginCard] auth0Client.handleRedirectCallback Error:', error);
      }
    } else {
      console.error('Login failed.', messageFromDialog);
    }
  };

  const goBackToLogin = () => {
    setView('login');
    setSsoError(null);
    setSsoAuthOptions(null);
    setSubdomain('');
  };

  const handleSubdomainChange = (value: string) => {
    setSubdomain(value);
    if (ssoAuthOptions || ssoError) {
      setSsoAuthOptions(null);
      setSsoError(null);
    }
  };

  async function handleLogin(e?: any, product?: string, organisationId?: string) {
    e?.preventDefault();

    const connection = product ? 'SSO' : 'Username-Password-Authentication';
    const resource = product && organisationId ? `tenant=${organisationId}&product=${product}` : undefined;

    await loginWithRedirect({
      authorizationParams: {
        connection: connection,
        ...(connection === 'SSO' ? { resource } : {}),
        redirect_uri: location.origin + '/plugins/outlook/dialog',
      },
      openUrl: async (url: string) => {
        try {
          const redirect = encodeURIComponent(url);
          const bounceURL = location.origin + '/plugins/outlook/dialog?redirect=' + redirect;
          openOfficeDialog(bounceURL, {}, processMessage);
        } catch (error) {
          console.error('Error opening URL:', error);
        }
      },
    });
  }

  async function handleSsoSubmit(e: FormEvent) {
    e.preventDefault();
    if (!subdomain.trim() || ssoLoading) return;

    const { inputValue, discoveryDomain } = normalizeSubdomainInput(subdomain);
    if (!discoveryDomain) {
      setSsoError('Please enter a valid Tekst subdomain.');
      return;
    }

    // Normalise what the user sees as well (e.g. paste full URL -> just "tekst")
    setSubdomain(inputValue);

    setSsoLoading(true);
    const minDelay = new Promise((resolve) => setTimeout(resolve, 250));
    let nextAuthOptions: AuthOptions | null = ssoAuthOptions;
    let nextError: string | null = ssoError;

    try {
      const url = `${getApiUrl()}/api/v2/public/organisation/authoptions?domain=${encodeURIComponent(discoveryDomain)}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (res.status === 404) {
        nextAuthOptions = null;
        nextError = 'Subdomain not found';
      } else if (!res.ok) {
        throw new Error('Failed to get auth options for subdomain');
      } else {
        const data: AuthOptions = await res.json();
        nextAuthOptions = data;

        const hasSsoForOrg = data.authMethods.some((method) => method.type === 'SSO');
        nextError = hasSsoForOrg ? null : 'No SSO options for this subdomain';
      }
    } catch {
      nextAuthOptions = null;
      nextError = 'Something went wrong while looking up your subdomain. Please try again.';
    } finally {
      await minDelay;
      setSsoAuthOptions(nextAuthOptions);
      setSsoError(nextError);
      setSsoLoading(false);
    }
  }

  return (
    <div className="w-full h-full overflow-y-auto scrollbar-hide">
      <div className="min-h-full flex flex-col items-center justify-center py-4">
        <div className="h-fit flex flex-col items-center justify-center w-full gap-2">
          <Card className="w-full px-5 pt-6 pb-6">
            <div className="flex items-center shrink-0 gap-4 mx-auto justify-center mb-4 pr-3 h-[20px]">
              <img
                src="/static/images/tekst-logo.svg"
                width={100}
                height={100}
                alt="Tekst logo"
                className="w-auto h-[18px]"
              />
            </div>
            {view === 'login' ? (
              <>
                <span className="text-sm text-neutral-400 text-center leading-relaxed max-w-xs mx-auto block">
                  Work with Tekst directly from Microsoft Outlook - use custom tailored AI models to analyse and
                  automate all incoming messages
                </span>
                <Separator className="my-4" />
                <form onSubmit={handleLogin} className="flex flex-col gap-4 w-full">
                  <Button type="submit" disabled={isDialogOpen}>
                    {isDialogOpen ? (
                      <>
                        Processing
                        <AnimatedEllipsis />
                      </>
                    ) : (
                      'Log in'
                    )}
                  </Button>
                  {showSsoButton && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full gap-2"
                      disabled={isDialogOpen}
                      onClick={() => {
                        console.log('[LoginCard] "Log in via SSO" clicked');
                        setView('sso');
                      }}
                    >
                      <span className="truncate">Log in via SSO</span>
                      <span className="flex items-center gap-1.5">
                        <img
                          src={identityProviders.microsoft.image}
                          width={18}
                          height={18}
                          alt={identityProviders.microsoft.shortLabel}
                        />
                        <img
                          src={identityProviders.google.image}
                          width={18}
                          height={18}
                          alt={identityProviders.google.shortLabel}
                        />
                      </span>
                    </Button>
                  )}
                  <p className="text-sm text-neutral-400 text-center">
                    No account yet?
                    <a
                      href="https://www.tekst.com/talk-to-tekst?ref=login"
                      target="_blank"
                      rel="noreferrer"
                      className="hover:text-primary underline underline-offset-1 cursor-pointer transition-colors ml-1"
                    >
                      Book a demo
                    </a>
                  </p>
                </form>
              </>
            ) : (
              <>
                <div className="mt-1 mb-5 w-full text-center">
                  <p className="text-base font-medium text-neutral-900">Log in with SSO</p>
                </div>
                <form onSubmit={handleSsoSubmit} className="flex flex-col gap-4 w-full">
                  <InputWithLabel
                    label={
                      <div className="flex items-center gap-1.5">
                        <span>Tekst subdomain</span>
                        <WithTooltip
                          tooltip="Use the subdomain your team uses to access Tekst, e.g. 'company' in company.tekst.com."
                          side="top"
                        >
                          <span className="inline-flex items-center">
                            <HelpCircle size={14} className="text-neutral-400" />
                          </span>
                        </WithTooltip>
                      </div>
                    }
                    placeholder="company"
                    value={subdomain}
                    onChange={(e) => handleSubdomainChange(e.target.value)}
                    endadornment={<span className="text-sm text-neutral-400">.tekst.com</span>}
                    className={ssoError ? '[&>div]:border-destructive [&>div]:bg-destructive/5' : undefined}
                  />
                  {ssoError && (
                    <div className="flex items-center gap-2 rounded-sm border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                      <XCircle size={18} className="shrink-0" />
                      <div className="leading-snug">
                        <p className="font-medium">
                          {ssoError === 'Subdomain not found' || ssoError === 'No SSO options for this subdomain'
                            ? ssoError
                            : 'Something went wrong'}
                        </p>
                        {ssoError !== 'Subdomain not found' && ssoError !== 'No SSO options for this subdomain' && (
                          <p className="mt-0.5 text-xs">Please try again or use a different login method.</p>
                        )}
                      </div>
                    </div>
                  )}
                  {!ssoAuthOptions || !hasSsoMethods ? (
                    <Button type="submit" disabled={!subdomain.trim() || ssoLoading}>
                      {ssoLoading ? (
                        <>
                          Looking up organisation
                          <AnimatedEllipsis />
                        </>
                      ) : ssoError ? (
                        'Try again'
                      ) : (
                        'Continue'
                      )}
                    </Button>
                  ) : null}
                  {ssoError && (
                    <Button type="button" variant="outline" className="w-full" onClick={goBackToLogin}>
                      Log in with a password
                    </Button>
                  )}
                  {ssoAuthOptions &&
                    !ssoError &&
                    (hasSsoMethods ? (
                      <SsoMethodButtons
                        methods={ssoAuthOptions.authMethods}
                        getLabel={(name) => `Use ${name}`}
                        onClick={(e, method) => handleLogin(e, method.id, ssoAuthOptions.organisationId)}
                      />
                    ) : (
                      <div className="w-full flex items-center justify-center my-1">
                        <div className="w-full h-[1px] bg-destructive/40" />
                        <span className="text-destructive text-xs px-2 uppercase">No SSO options</span>
                        <div className="w-full h-[1px] bg-destructive/40" />
                      </div>
                    ))}
                  <button
                    type="button"
                    onClick={goBackToLogin}
                    className="text-xs text-neutral-400 mx-auto hover:text-neutral-600 hover:underline underline-offset-2"
                  >
                    Go back to login
                  </button>
                </form>
              </>
            )}
            <div className="mx-auto flex items-center justify-evenly gap-3 mt-6">
              <a target="_blank" href="https://trust.tekst.com/" rel="noopener noreferrer">
                <div className="text-neutral-400 text-xs flex flex-col  items-center gap-1 cursor-pointer hover:underline">
                  <GlobeLock size={14} />
                  SOC 2
                </div>
              </a>
              <a
                target="_blank"
                href="https://assets-global.website-files.com/6560a9f62d4104b1c8efc149/659eb9956e73f13d3dfef210_Tekst.ai%20%20-%20ISO%20IEC%2027001%202022%20Official%20Certificate%20(1).pdf"
                rel="noopener noreferrer"
              >
                <div className="text-neutral-400 text-xs flex flex-col items-center gap-1 cursor-pointer hover:underline">
                  <ShieldCheck size={14} />
                  ISO 27001
                </div>
              </a>
              <a target="_blank" href="https://trust.tekst.com/" rel="noopener noreferrer">
                <div className="text-neutral-400 text-xs flex flex-col  items-center gap-1 cursor-pointer hover:underline">
                  <Handshake size={14} />
                  GDPR
                </div>
              </a>
            </div>
          </Card>
          <div className="mx-auto text-neutral-400 text-xs shrink-0 flex-center h-8 hover:underline">
            <a target="_blank" href="https://www.tekst.com/legal/privacy-policy" rel="noopener noreferrer">
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
